import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import apiService from "../services/api.service";
import { darkenHex } from "../utils/assets";

export const PLATFORM_APPLICATION = {
  slug: null,
  name: "WebinarPlus",
  logo_url: "/logowebinarplusbianco.png",
  site_title: "WebinarPlus",
  site_subtitle: "",
  primary_color: "#0F172A",
  secondary_color: "#0F172A",
  facebook_url: null,
  youtube_url: null,
  instagram_url: null,
  footer_links: [],
  social_links: [],
  support_email: "info@ceformedsrl.it",
  support_phone: "",
  registration_fields: [],
};

const ApplicationContext = createContext({
  application: PLATFORM_APPLICATION,
  loading: true,
  refreshApplication: async () => {},
  applyApplication: () => {},
});

function tenantPlaceholder(slug) {
  return {
    ...PLATFORM_APPLICATION,
    slug,
    name: "",
    logo_url: "",
    site_title: "",
    site_subtitle: "",
  };
}

function applyTheme(application) {
  const primary = application.primary_color || "#003274";
  const secondary = application.secondary_color || "#202a2e";
  document.documentElement.style.setProperty("--platform-primary", primary);
  document.documentElement.style.setProperty("--platform-primary-hover", darkenHex(primary));
  document.documentElement.style.setProperty("--platform-secondary", secondary);
}

export function ApplicationProvider({ slug, children }) {
  const [application, setApplication] = useState(() =>
    slug ? tenantPlaceholder(slug) : PLATFORM_APPLICATION
  );
  const [loading, setLoading] = useState(true);

  const applyApplication = useCallback((nextApplication) => {
    const merged = { ...PLATFORM_APPLICATION, ...nextApplication };
    setApplication(merged);
    applyTheme(merged);
    document.title = merged.site_title || merged.name || "WebinarPlus";
  }, []);

  const refreshApplication = useCallback(async () => {
    if (!slug) {
      applyApplication(PLATFORM_APPLICATION);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await apiService.getApplicationBySlug(slug);
      applyApplication(response.data);
    } catch {
      applyApplication(tenantPlaceholder(slug));
    } finally {
      setLoading(false);
    }
  }, [slug, applyApplication]);

  useEffect(() => {
    refreshApplication();
  }, [refreshApplication]);

  return (
    <ApplicationContext.Provider value={{ application, loading, refreshApplication, applyApplication }}>
      {children}
    </ApplicationContext.Provider>
  );
}

export function useApplication() {
  return useContext(ApplicationContext);
}

export default ApplicationContext;
